import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fadeIn } from '@/utils/animations';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import AutoLaunchSection from '@/components/AutoLaunchSection';
import EmailComposerSection from '@/components/EmailComposerSection';
import IntelligentOptimizationSection from '@/components/IntelligentOptimizationSection';
import AutomatedLeadHandlingSection from '@/components/AutomatedLeadHandlingSection';
import EmailAdPlatformSection from '@/components/EmailAdPlatformSection';
import SmartToolsSection from '@/components/SmartToolsSection';
import MasterDashboardSection from '@/components/MasterDashboardSection';
import ManualCampaignSection from '@/components/ManualCampaignSection';
import StatsSection from '@/components/StatsSection';
import Footer from '@/components/Footer';
import { useIsMobile } from '@/hooks/use-mobile';

const NoPricingLanding = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setIsLoaded(true);
    console.log('✨ No-Pricing Landing Page Loaded! ✨');
  }, []);

  // Static background effects for better performance
  const StaticBackgroundEffects = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Static gradient orbs - no animation */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-purple-500/15 via-blue-500/8 to-pink-500/15 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-[400px] h-[400px] bg-gradient-to-l from-blue-500/12 via-cyan-500/8 to-purple-500/12 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-indigo-500/8 via-purple-500/4 to-pink-500/8 rounded-full blur-3xl" />
    </div>
  );

  // Hero section with demo focus
  const NoPricingHero = () => (
    <section className="min-h-screen flex flex-col justify-center items-center text-center px-4 sm:px-6 py-20 relative overflow-hidden">
      {/* Optimized gradient background with GPU acceleration */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950"
        style={{ transform: 'translate3d(0,0,0)' }}
      />
      
      {/* Simplified gradient mesh with better performance */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          background: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(147,51,234,0.2) 0%, transparent 50%)',
          transform: 'translate3d(0,0,0)',
          willChange: 'auto'
        }}
      />
      
      <motion.div 
        className="max-w-6xl mx-auto relative z-10"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: "easeOut", type: "tween" }}
        style={{ willChange: 'transform' }}
      >
        {/* Optimized status badge */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, type: "tween" }}
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-indigo-500/15 via-purple-500/10 to-pink-500/15 backdrop-blur-sm border border-indigo-500/30 mb-8 shadow-xl group hover:scale-105 transition-transform duration-300"
          style={{ willChange: 'transform' }}
        >
          <div className="relative">
            <span className="inline-flex h-3 w-3 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
          </div>
          <span className="text-sm font-semibold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Limited Access Beta
          </span>
        </motion.div>
        
        {/* Optimized main title */}
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: "easeOut", type: "tween" }}
          className="text-6xl md:text-8xl font-bold mb-8 leading-tight"
          style={{ willChange: 'transform' }}
        >
          <span className="block text-white">
            The Fully Automated
          </span>
          <span className="block mt-4 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent gradient-text-animation">
            Marketer
          </span>
        </motion.h1>
        
        {/* Enhanced subtitle */}
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: "easeOut", type: "tween" }}
          className="text-xl md:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed"
          style={{ willChange: 'transform' }}
        >
          Vibe your way to MRR with scaled cold email.{' '}
          <span className="text-white font-semibold">
            AI executes every campaign
          </span>{' '}
          from beginning to end so you can scale without headache.
        </motion.p>
        
        {/* Simple decorative divider */}
        <motion.div 
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.5, type: "tween" }}
          className="flex justify-center mb-16"
          style={{ willChange: 'transform' }}
        >
          <div className="relative">
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full shadow-xl" />
          </div>
        </motion.div>
        
        {/* Optimized video container */}
        <motion.div 
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.6, ease: "easeOut", type: "tween" }}
          className="w-full px-0 sm:px-6 md:max-w-4xl mx-auto mb-12 sm:mb-16 relative group"
          style={{ willChange: 'transform' }}
        >
          {/* Optimized glow effect */}
          <div 
            className="absolute -inset-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ transform: 'translate3d(0,0,0)' }}
          />
          
          {/* Video wrapper */}
          <div className="relative overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10 backdrop-blur-sm">
            {/* Inner content */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-gray-900/90 to-gray-950/90">
              <AspectRatio ratio={16/9} className="w-full relative">
                <iframe 
                  src="https://player.vimeo.com/video/1069192829?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479"
                  width="100%" 
                  height="100%" 
                  frameBorder="0" 
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                  title="Live Lead Dashboard"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  className="rounded-3xl"
                />
              </AspectRatio>
            </div>
          </div>
        </motion.div>
        
        {/* Demo-focused action buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8, ease: "easeOut", type: "tween" }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6"
          style={{ willChange: 'transform' }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "tween", duration: 0.2 }}
            style={{ willChange: 'transform' }}
          >
            <Link to="/book-demo" className="group relative optimized-button">
              <div className="relative inline-flex h-16 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-12 text-lg font-semibold text-white shadow-xl transition-all duration-300">
                <span className="relative z-10 flex items-center">
                  Book a Demo
                  <ArrowRight size={20} className="ml-3" />
                </span>
              </div>
            </Link>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "tween", duration: 0.2 }}
            style={{ willChange: 'transform' }}
          >
            <Link to="/pricing-info" className="group relative optimized-button">
              <div className="relative inline-flex h-16 items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border-2 border-white/20 px-12 text-lg font-semibold text-white shadow-xl transition-all duration-300 hover:bg-white/10 hover:border-purple-500/50">
                <span className="relative z-10">Get Pricing</span>
              </div>
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* CSS-only gradient animation styles */}
      <style>{`
        .gradient-text-animation {
          background-size: 200% 200%;
          animation: gradientShift 3s ease-in-out infinite;
        }
        
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        .optimized-button {
          transform: translate3d(0,0,0);
          will-change: transform;
        }
        
        .optimized-button > div {
          transform: translate3d(0,0,0);
        }
      `}</style>
    </section>
  );

  return (
    <motion.div 
      className="min-h-screen bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950 relative overflow-hidden"
      initial="initial"
      animate="animate"
      variants={fadeIn}
    >
      {/* Simplified background layers */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-purple-900/20 via-transparent to-transparent" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent" />
      
      {/* Static background effects for better performance */}
      <StaticBackgroundEffects />
      
      {/* Static grid pattern (no animation) */}
      <div 
        className="fixed inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}
      />
      
      {/* Main content with simplified styling */}
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/5 to-transparent" />
          <NoPricingHero />
        </div>
        
        {/* AutoLaunch Section */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/3 to-pink-500/5" />
          <AutoLaunchSection />
        </div>
        
        {/* Features section */}
        <section className="section relative py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/5 to-transparent" />
          
          <div className="max-w-7xl mx-auto px-6 relative">
            <div className="text-center mb-20">
              {/* Simplified badge */}
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-blue-500/10 via-purple-500/5 to-pink-500/10 backdrop-blur-sm border border-blue-500/20 mb-6 shadow-xl hover:scale-105 transition-transform duration-300">
                <span className="w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" />
                <span className="text-sm font-semibold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  AI-Powered Revolution
                </span>
              </div>
              
              {/* Title */}
              <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Experience the{' '}
                <span className="relative">
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Magic
                  </span>
                </span>
              </h2>
              
              {/* Subtitle */}
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Witness the future of automated marketing unfold before your eyes
              </p>
            </div>
          </div>
        </section>
        
        {/* Feature sections */}
        <div className="space-y-0">
          {[
            { component: EmailComposerSection, gradient: 'from-blue-500/0 via-blue-500/5 to-blue-500/0' },
            { component: IntelligentOptimizationSection, gradient: 'from-purple-500/0 via-purple-500/5 to-purple-500/0' },
            { component: AutomatedLeadHandlingSection, gradient: 'from-pink-500/0 via-pink-500/5 to-pink-500/0' },
            { component: EmailAdPlatformSection, gradient: 'from-indigo-500/0 via-indigo-500/5 to-indigo-500/0' },
            { component: SmartToolsSection, gradient: 'from-teal-500/0 via-teal-500/5 to-teal-500/0' },
          ].map(({ component: Component, gradient }, index) => (
            <div 
              key={index}
              className="relative py-16"
            >
              {/* Background layers */}
              <div className={`absolute inset-0 bg-gradient-to-r ${gradient}`} />
              
              {/* Subtle borders */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
              
              <div className="relative">
                <Component />
              </div>
            </div>
          ))}
        </div>
        
        {/* Dashboard section */}
        <section className="section relative py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/5 to-transparent" />
          
          <div className="max-w-7xl mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Command Center
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Your mission control for automated marketing campaigns
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-3xl border border-gray-700/50 p-2 shadow-2xl">
                  <div className="bg-gradient-to-br from-gray-900/90 to-gray-950/90 rounded-2xl p-8">
                    <MasterDashboardSection isInGrid={true} />
                  </div>
                </div>
              </div>
              
              <div className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-3xl border border-gray-700/50 p-2 shadow-2xl">
                  <div className="bg-gradient-to-br from-gray-900/90 to-gray-950/90 rounded-2xl p-8">
                    <ManualCampaignSection isInGrid={true} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Results section */}
        <section className="section relative py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 via-transparent to-blue-900/10" />
          
          <div className="max-w-7xl mx-auto px-6 relative">
            <h2 className="text-4xl md:text-6xl font-bold text-center mb-16">
              <span className="bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                Results &amp; Impact
              </span>
            </h2>
            
            {/* Stats section */}
            <div className="mb-32">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500/15 to-blue-500/15 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative bg-gradient-to-br from-gray-800/30 to-gray-900/30 rounded-3xl border border-gray-700/50 p-12 shadow-2xl">
                  <StatsSection hideTitle={true} />
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA Section - Modified to focus on demo */}
        <section className="section relative py-40 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 to-transparent" />
          
          <div className="max-w-5xl mx-auto text-center px-6 relative">
            <div className="relative group">
              {/* Outer glow on hover */}
              <div className="absolute -inset-8 bg-gradient-to-r from-blue-500/10 via-purple-500/20 to-pink-500/10 rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
              
              <div className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 rounded-[2.5rem] border border-gray-700/60 p-16 shadow-2xl">
                {/* Badge */}
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-purple-500/20 via-blue-500/10 to-pink-500/20 border border-purple-500/30 mb-8 shadow-xl hover:scale-105 transition-transform duration-300">
                  <span className="w-3 h-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full" />
                  <span className="text-sm font-semibold bg-gradient-to-r from-purple-400 via-blue-400 to-pink-400 bg-clip-text text-transparent">
                    Get Started Today
                  </span>
                </div>
                
                {/* Title */}
                <h2 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                  <span className="relative">
                    <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                      See It In Action
                    </span>
                  </span>
                </h2>
                
                {/* Subtitle */}
                <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
                  Book a personalized demo to see how Munger can transform your marketing efforts
                </p>

                {/* Demo-focused buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <Link
                    to="/book-demo"
                    className="group relative"
                  >
                    <div className="relative inline-flex h-14 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-12 text-lg font-semibold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-purple-500/30">
                      <span className="relative z-10 flex items-center">
                        Book a Demo
                        <span className="ml-2">→</span>
                      </span>
                    </div>
                  </Link>
                  
                  <Link
                    to="/pricing-info"
                    className="group relative"
                  >
                    <div className="relative inline-flex h-14 items-center justify-center rounded-full bg-transparent border-2 border-gray-600 px-12 text-lg font-semibold text-white shadow-xl transition-all duration-300 hover:border-purple-500/70 hover:bg-purple-500/10 hover:scale-105">
                      <span className="relative z-10">Get Pricing</span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    </motion.div>
  );
};

export default NoPricingLanding; 